import { Injectable } from '@angular/core';
import { Config } from '../app.configs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { RainCard } from '../models/card.model';
import { PaginatedCardTransactions } from '../interfaces/paginated-card-transaction.interface';
import { CacheService } from './cache.service';

export type CardTransactionType =
  | 'CARD_PAYMENT'
  | 'CARD_FUNDING'
  | 'CARD_REFUND'
  | 'CARD_WITHDRAWAL';

export interface CardTransactionFilters {
  uuid?: string;
  startDate?: string; // YYYY-MM-DD, Europe/Paris day
  endDate?: string; // YYYY-MM-DD, Europe/Paris day
  transactionType?: CardTransactionType | '';
  merchant?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CardService {
  private readonly CACHE_PREFIX_CARDS = 'cards:';
  private readonly CACHE_PREFIX_CARD_TXS = 'card-txs:';

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private cacheService: CacheService,
  ) {}

  getCards(
    uuid: string,
    forceRefresh: boolean = false,
  ): Observable<RainCard[]> {
    const cacheKey = `${this.CACHE_PREFIX_CARDS}${uuid}`;

    return this.cacheService.get<RainCard[]>(
      cacheKey,
      () => {
        const headers = this.authService.getHeaders();
        return this.http.get<RainCard[]>(
          Config.toApiUrl(`dashboard/users/${uuid}/cards/`),
          { headers, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  getCardTransactions(
    uuid: string,
    page: number = 1,
    filters: CardTransactionFilters = {},
    forceRefresh: boolean = false,
  ): Observable<PaginatedCardTransactions> {
    let params = new HttpParams().set('page', page);
    const uuidFilter = filters.uuid?.trim();
    const merchant = filters.merchant?.trim();
    if (uuidFilter) params = params.set('uuid', uuidFilter);
    if (filters.startDate) params = params.set('start_date', filters.startDate);
    if (filters.endDate) params = params.set('end_date', filters.endDate);
    if (filters.transactionType)
      params = params.set('transaction_type', filters.transactionType);
    if (merchant) params = params.set('merchant', merchant);

    const cacheKey = `${this.CACHE_PREFIX_CARD_TXS}${uuid}:${params.toString()}`;

    return this.cacheService.get<PaginatedCardTransactions>(
      cacheKey,
      () => {
        const headers = this.authService.getHeaders();
        return this.http.get<PaginatedCardTransactions>(
          Config.toApiUrl(`dashboard/users/${uuid}/card-transactions/`),
          { headers, params, withCredentials: true },
        );
      },
      undefined,
      forceRefresh,
    );
  }

  invalidateCardsCache(uuid: string): void {
    this.cacheService.invalidate(`${this.CACHE_PREFIX_CARDS}${uuid}`);
    this.cacheService.invalidateByPrefix(
      `${this.CACHE_PREFIX_CARD_TXS}${uuid}`,
    );
  }

  createCard(uuid: string): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/create-rain-user/`),
        {},
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateCardsCache(uuid)));
  }

  createRainUser(uuid: string): Observable<any> {
    return this.createCard(uuid);
  }

  migrateCard(uuid: string, force: boolean = false): Observable<any> {
    const headers = this.authService.getHeaders();

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/migrate-card/`),
        { force },
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateCardsCache(uuid)));
  }

  manageCard(uuid: string, card_uuid: string, action: string): Observable<any> {
    const headers = this.authService.getHeaders();

    const payload = {
      action: action,
    };

    return this.http
      .post(
        Config.toApiUrl(`dashboard/users/${uuid}/card/${card_uuid}/`),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateCardsCache(uuid)));
  }

  updateCardLimit(
    uuid: string,
    card_uuid: string,
    amount: number,
  ): Observable<any> {
    const headers = this.authService.getHeaders();

    const payload = {
      amount: amount,
    };

    return this.http
      .post(
        Config.toApiUrl(
          `dashboard/users/${uuid}/card/${card_uuid}/update-limit/`,
        ),
        payload,
        { headers, withCredentials: true },
      )
      .pipe(tap(() => this.invalidateCardsCache(uuid)));
  }
}
